/**
 * Financial Diagnostic Wizard API endpoints
 * Including multi-goal roadmap and arbitrage execution
 */

import apiClient from '../client';
import {
  FinancialDiagnosticStartResponse,
  FinancialDiagnosticSubmitData,
  FinancialDiagnosticSubmitResponse,
  GoalConfirmRequest,
  GoalConfirmResponse,
  GoalsConfirmAllRequest,
  GoalsConfirmAllResponse,
  ExecuteArbitrageResponse,
  DiagnosticActionResponse,
  SavingsTypesResponse,
} from '../types';

export const financialDiagnosticApi = {
  /**
   * Get current financial profile (to pre-fill wizard)
   * GET /ai/financial_profile
   */
  getFinancialProfile: async (): Promise<any> => {
    const response = await apiClient.get('/ai/financial_profile');
    return response.data;
  },

  /**
   * Get available savings types for user's country
   * GET /ai/snapshot/savings_types
   */
  getSavingsTypes: async (): Promise<SavingsTypesResponse> => {
    const response = await apiClient.get<SavingsTypesResponse>('/ai/snapshot/savings_types');
    return response.data;
  },

  /**
   * Start the financial diagnostic wizard
   * POST /ai/snapshot/start (API URL unchanged)
   */
  startDiagnostic: async (): Promise<FinancialDiagnosticStartResponse> => {
    const response = await apiClient.post<FinancialDiagnosticStartResponse>(
      '/ai/snapshot/start'
    );
    return response.data;
  },

  /**
   * Submit financial diagnostic wizard data (step-by-step or bulk)
   * POST /ai/snapshot/submit (API URL unchanged)
   */
  submitDiagnostic: async (
    data: FinancialDiagnosticSubmitData
  ): Promise<FinancialDiagnosticSubmitResponse> => {
    const response = await apiClient.post<FinancialDiagnosticSubmitResponse>(
      '/ai/snapshot/submit',
      data
    );
    return response.data;
  },

  /**
   * Confirm AI-proposed financial goal from diagnostic
   * POST /financial_goals/confirm_diagnostic
   */
  confirmDiagnosticGoal: async (
    request: GoalConfirmRequest
  ): Promise<GoalConfirmResponse> => {
    const response = await apiClient.post<GoalConfirmResponse>(
      '/financial_goals/confirm_diagnostic',
      request
    );
    return response.data;
  },

  /**
   * Confirm all goals from a multi-goal roadmap
   * POST /financial_goals/confirm_all
   */
  confirmAllGoals: async (
    request: GoalsConfirmAllRequest
  ): Promise<GoalsConfirmAllResponse> => {
    const response = await apiClient.post<GoalsConfirmAllResponse>(
      '/financial_goals/confirm_all',
      request
    );
    return response.data;
  },

  /**
   * Execute debt arbitrage - mark debt as repaid and update profile
   * POST /financial_goals/:id/execute_arbitrage
   */
  executeArbitrage: async (
    goalId: string
  ): Promise<ExecuteArbitrageResponse> => {
    const response = await apiClient.post<ExecuteArbitrageResponse>(
      `/financial_goals/${goalId}/execute_arbitrage`
    );
    return response.data;
  },

  /**
   * Handle diagnostic conversation actions (2-step flow)
   * POST /diagnostic/action
   *
   * Actions:
   * - send_action_plan: Request action plan with goal buttons
   * - ask_question: Open chat for questions
   * - confirm: Confirm diagnostic, proceed to goals
   * - correct: Request correction form
   */
  diagnosticAction: async (
    action: 'send_action_plan' | 'ask_question' | 'confirm' | 'correct',
    payload?: Record<string, any>,
    messageId?: string
  ): Promise<DiagnosticActionResponse> => {
    const response = await apiClient.post<DiagnosticActionResponse>(
      '/diagnostic/action',
      {
        action,
        payload: payload || {},
        message_id: messageId,
      }
    );
    return response.data;
  },
};

// Legacy alias for backward compatibility
/** @deprecated Use financialDiagnosticApi instead */
export const snapshotApi = financialDiagnosticApi;
